import React from "react";
import { CardBody, Input } from "reactstrap";
import withStyles from "isomorphic-style-loader/withStyles";
import styles from "../../styles.scss";
import TooltipColorPicker from "../../common/tooltipColorPicker";
import SectionLabel from "../../common/sectionLabel";
import ImageSelectionInput from "../../common/imageSelectInput";
import { LeftSidebarVariablesProp } from "../../common/types/variables";
import { CharityAssetsService } from "../../../../../../../../../services/charityAssetsService";


const NewsfeedNavbarSection = ({ variables, charityId, firebase, onError }: LeftSidebarVariablesProp) => {
  const [giveButtonLabel, setGiveButtonLabel] = variables["giveButtonLabel"];
  const [giveButtonColor, setGiveButtonColor] = variables["giveButtonColor"];
  const [highlightColor, setHighlightColor] = variables["highlightColor"];
  const [logo, setLogo] = variables["logo"];
  const [favicon, setFavicon] = variables["favicon"];
  const [showHeaderTitle, setShowHeaderTitle] = variables["showHeaderTitle"];
  const [headerTitle, setHeaderTitle] = variables["headerTitle"];
  const [organizationName, setOrganizationName] = variables["organizationName"];

  const handleLogoUpload = async (file: File): Promise<string> => {
    if (!charityId) {
      throw new Error('Charity ID is required for logo upload');
    }
    if (!firebase) {
      throw new Error('Firebase instance is required for logo upload');
    }
    const assetsService = new CharityAssetsService(firebase);
    return await assetsService.uploadLogo(file, charityId, 'navbar');
  };

  const handleFaviconUpload = async (file: File): Promise<string> => {
    if (!charityId) {
      throw new Error('Charity ID is required for favicon upload');
    }
    if (!firebase) {
      throw new Error('Firebase instance is required for favicon upload');
    }
    const assetsService = new CharityAssetsService(firebase);
    return await assetsService.uploadIcon(file, charityId, 'favicon');
  };

  return (
    <CardBody>
      <ImageSelectionInput
        label={
          <SectionLabel
            label="Logo"
            tooltip="Change the logo and add the logo you want for your charity"
            subtitle="Max. size 300×200 px. Format SVG, JPG, PNG."
          />
        }
        image={logo}
        setImage={setLogo}
        onUpload={handleLogoUpload}
        defaultImage="https://placehold.co/300x200/000000/FFFFFF/png"
        altText="Logo"
        onError={onError}
      />
      <div className={styles.headerTitleOption}>
        <div className={`adminCheckboxRow ${styles.checkboxLabel}`}>
          <Input
            type="checkbox"
            id="showHeaderTitleInNavbar"
            checked={!!showHeaderTitle}
            onChange={e => setShowHeaderTitle(e.target.checked)}
          />
          <label htmlFor="showHeaderTitleInNavbar" />
          <span className={styles.checkboxText}>Show title in header</span>
        </div>
        {showHeaderTitle && (
          <>
            <SectionLabel
              label="Header title"
              tooltip="Text shown next to the logo in the newsfeed header."
            />
            <Input
              type="text"
              value={headerTitle ?? ""}
              placeholder={organizationName || "Organization name"}
              onChange={e => setHeaderTitle(e.target.value)}
              className={styles.input}
            />
          </>
        )}
      </div>
      <ImageSelectionInput
        label={
          <SectionLabel
            label="Icon"
            subtitle="Your website icon. ICO, PNG, JPG or SVG."
          />
        }
        image={favicon}
        setImage={setFavicon}
        onUpload={handleFaviconUpload}
        accept="image/x-icon,image/vnd.microsoft.icon,.ico,image/png,image/jpeg,image/jpg,image/svg+xml"
        defaultImage="https://placehold.co/64x64/333333/FFFFFF/png?text=Icon"
        altText="Icon"
        onError={onError}
      />
      <div className={styles.section}>
        <SectionLabel
          label="Title"
          tooltip="Site title used in the browser tab."
        />
        <Input
          type="text"
          value={organizationName ?? ""}
          placeholder="Organization name"
          onChange={e => setOrganizationName(e.target.value)}
          className={styles.input}
        />
      </div>
      <div className={styles.section}>
        <SectionLabel
          label="Give Button label"
          tooltip="Change the text of your 'Give' button"
        />
        <Input
          type="text"
          value={giveButtonLabel}
          onChange={e => setGiveButtonLabel(e.target.value)}
          className={styles.input}
        />
        <SectionLabel
          label="Give Button color"
          tooltip="Personalize the color of the 'Give' button"
        />
        <TooltipColorPicker
          value={giveButtonColor}
          setValue={setGiveButtonColor}
        />
      </div>

      <div className={styles.section}>
        <SectionLabel
          label="Highlight color"
          tooltip="Personalize the color of the highlights throughout your charity's page"
        />
        <TooltipColorPicker
          value={highlightColor}
          setValue={setHighlightColor}
        />
      </div>
    </CardBody>
  );
};

export default withStyles(styles)(NewsfeedNavbarSection);
